import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from "react";
import { LiveKitRoom, useRoomContext } from "@livekit/components-react";
import { SimpleCallInterface } from "./SimpleCallInterface";
import { Room, RemoteParticipant } from "livekit-client";
import { fetchLiveKitToken } from "./livekit-utils";
import { getCookie } from "../../../../api/cookie";
import { logger } from "../../../../utils/logger";
import { generateRoomName, cleanPhoneNumber, getPhoneNumberId, clearIncomingCallData } from "../../../../utils/callUtils";
import { stopCallNotification } from "../../../../utils/audioNotification";
import { deleteRoom } from "../../../../services/roomService";
import { CallHeader } from "./CallHeader";
import { CallStatusView } from "./CallStatusView";
import { getCallAudio, getWhatsAppCallRecords, getCallRecordsFromAudioApi, initiateBusinessCall, listLiveKitRooms } from "../../../../api/login.service";
import AudioPlayer from "react-h5-audio-player";
import { RHAP_UI } from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";

// Component to monitor for remote participants joining
const RoomParticipantMonitor: React.FC<{ callInitiated: boolean; onRemoteParticipantJoined: () => void }> = ({ callInitiated, onRemoteParticipantJoined }) => {
  const room = useRoomContext();
  
  useEffect(() => {
    if (!room || !callInitiated) return;
    
    const handleParticipantConnected = (participant: RemoteParticipant) => {
      logger.log("Remote participant connected:", participant.identity);
      // Remote participant joined - auto-join the call
      onRemoteParticipantJoined();
    };
    
    // Check if remote participants already exist
    const remoteParticipants = Array.from(room.remoteParticipants.values());
    if (remoteParticipants.length > 0) {
      logger.log("Remote participants already in room:", remoteParticipants.length);
      onRemoteParticipantJoined();
      return;
    }
    
    // Listen for new remote participants
    room.on('participantConnected', handleParticipantConnected);
    
    return () => {
      room.off('participantConnected', handleParticipantConnected);
    };
  }, [room, callInitiated, onRemoteParticipantJoined]);
  
  return null;
};

interface WhatsAppCallsTabProps {
  customerData: { phone: string; name: string } | null;
  participantIdentity?: string | null;
  participantName?: string | null;
}

interface CallRecord {
  customerName?: string;
  customerNumber: string;
  timestamp: string;
  duration?: number;
  type?: 'incoming' | 'outgoing';
  status?: 'completed' | 'missed' | 'failed';
  callerId?: string;
  callId?: string;
  call_uuid?: string;
  recording_url?: string;
  call_start_time?: string;
  call_end_time?: string;
  call_duration_seconds?: number;
  call_direction?: string;
  caller_name?: string;
}

export function WhatsAppCallsTab({
  customerData,
  participantIdentity,
  participantName,
}: WhatsAppCallsTabProps) {
  
  const [isCalling, setIsCalling] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string>("");
  const [isConnected, setIsConnected] = useState(false);
  const [shouldConnect, setShouldConnect] = useState(false);
  const [currentRoomName, setCurrentRoomName] = useState<string | null>(null);
  const [callInitiated, setCallInitiated] = useState(false); // Track if call was initiated but not joined (waiting for WebSocket)
  const roomRef = useRef<Room | null>(null);
  const [, forceUpdate] = useState(0); // Force re-render helper
  
  // State for incoming call info - explicitly initialize to false
  const [incomingCallRoom, setIncomingCallRoom] = useState<string | null>(null);
  const [hasIncomingCall, setHasIncomingCall] = useState(false);
  const [incomingCallerPhone, setIncomingCallerPhone] = useState<string | null>(null);
  const [incomingCallTimestamp, setIncomingCallTimestamp] = useState<number | null>(null);
  const autoRejectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const outgoingCallPollIntervalRef = useRef<NodeJS.Timeout | null>(null); // For polling room status every 10 seconds
  const outgoingCallPollAttemptsRef = useRef<number>(0); // Track number of polling attempts (max 20)
  
  // State for call records
  const [callRecords, setCallRecords] = useState<CallRecord[]>([]);
  const [callRecordsLoading, setCallRecordsLoading] = useState(true);
  
  // Check if there's an incoming call today
  const hasIncomingCallToday = useMemo(() => {
    if (!callRecords || callRecords.length === 0) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    // Check if any call record is:
    // 1. Incoming (type === 'incoming' or call_direction === 'inbound'/'incoming')
    // 2. From today (call_start_time or timestamp is today)
    return callRecords.some((call) => {
      const isIncoming = call.type === 'incoming' || 
                        call.call_direction === 'inbound' || 
                        call.call_direction === 'incoming';
      
      if (!isIncoming) return false;
      
      // Get call date
      const callDate = call.call_start_time 
        ? new Date(call.call_start_time)
        : new Date(call.timestamp);
      
      callDate.setHours(0, 0, 0, 0); // Start of call day
      
      // Check if call is from today
      return callDate.getTime() === today.getTime();
    });
  }, [callRecords]);
  
  
  const handleCallWithRoom = useCallback(async (roomName: string) => {
    if (!roomName) return;
    
    // Ensure isCalling is true (might already be set by handleJoinIncomingCall)
    // But set it again to be safe and force re-render
    setIsCalling(true);
    forceUpdate(prev => prev + 1); // Force immediate re-render
    setCallError(null);
    
    try {
      
      // Stop the ringtone when connecting
      stopCallNotification();
      
      const participantToken = getCookie("token") || "";
      const phoneNumberId = getPhoneNumberId();
      const customerPhone = cleanPhoneNumber(customerData?.phone || "");
      
      if (!phoneNumberId) {
        throw new Error("phoneNumberId not found in sessionStorage");
      }
      
      if (!customerPhone) {
        throw new Error("Customer phone number not found");
      }
      
      // Generate room name if not already in correct format
      const finalRoomName = roomName.startsWith('user_') && roomName.includes('_calling_coming_') 
        ? roomName 
        : generateRoomName(phoneNumberId, customerPhone);
      
      // For incoming calls:
      // - YOUR name (local participant) = "You"
      // - Customer name is passed separately and will be shown for remote participant
      // Don't use random suffixes to avoid duplicate users
      const finalParticipantName = 'You'; // Your name when answering incoming call
      const finalParticipantIdentity = participantIdentity || `user_${customerPhone}`;
      
      const response = await fetchLiveKitToken(
        participantToken,
        finalRoomName,
        finalParticipantName,
        finalParticipantIdentity
      );
      
      setToken(response.token);
      setServerUrl(response.serverUrl);
      setCurrentRoomName(finalRoomName);
      setShouldConnect(true);
      
      // Store active call info immediately
      if (customerData) {
        sessionStorage.setItem('activeCallRoomName', finalRoomName);
        sessionStorage.setItem('activeCallCustomer', JSON.stringify(customerData));
        sessionStorage.setItem('activeCallTimestamp', Date.now().toString());
      }
      
      // Clear incoming call data and state
      clearIncomingCallData();
      setHasIncomingCall(false);
      setIncomingCallRoom(null);
      
      // Keep isCalling true until room connects (will be set to false in handleRoomConnected)
      
    } catch (err: any) {
      logger.error("Error connecting to call:", err);
      setCallError(err?.message || "Failed to connect to call");
      setIsCalling(false);
      setHasIncomingCall(false);
      setIncomingCallRoom(null);
    }
  }, [customerData, participantIdentity, participantName]);
  
  // Cleanup polling interval on component unmount
  useEffect(() => {
    return () => {
      if (outgoingCallPollIntervalRef.current) {
        clearInterval(outgoingCallPollIntervalRef.current);
        outgoingCallPollIntervalRef.current = null;
      }
      // Reset attempt counter
      outgoingCallPollAttemptsRef.current = 0;
    };
  }, []);
  
  // Listen for call connection events from GlobalCallConnection and sync state
  useEffect(() => {
    const handleCallConnected = (event: CustomEvent) => {
      const { roomName: connectedRoomName } = event.detail || {};
      const activeCallRoomName = sessionStorage.getItem('activeCallRoomName');
      
      // If the connected room matches our active call, update state immediately
      if (connectedRoomName && connectedRoomName === activeCallRoomName) {
        if (activeCallRoomName === currentRoomName || !currentRoomName) {
          setIsConnected(true);
          setIsCalling(false);
          forceUpdate(prev => prev + 1); // Force re-render
        }
      }
    };
    
    const handleCallEnded = (event: CustomEvent) => {
      const { roomName: endedRoomName, reason } = event.detail || {};
      const activeCallRoomName = sessionStorage.getItem('activeCallRoomName');
      
      // If the ended room matches our active call, disconnect immediately
      if (endedRoomName && endedRoomName === activeCallRoomName) {
        if (endedRoomName === currentRoomName || !currentRoomName) {
          logger.log('Call ended:', reason);
          setIsConnected(false);
          setIsCalling(false);
          setShouldConnect(false);
          setToken(null);
          setServerUrl("");
          setCurrentRoomName(null);
          
          // Clear active call storage
          sessionStorage.removeItem('activeCallRoomName');
          sessionStorage.removeItem('activeCallCustomer');
          sessionStorage.removeItem('activeCallTimestamp');
          
          forceUpdate(prev => prev + 1);
        }
      }
    };
    
    // Also periodically check if call is connected (for cases where event might be missed)
    const checkConnectionStatus = () => {
      const activeCallRoomName = sessionStorage.getItem('activeCallRoomName');
      const activeCallTimestamp = sessionStorage.getItem('activeCallTimestamp');
      
      if (activeCallRoomName && activeCallTimestamp && shouldConnect) {
        const timestamp = parseInt(activeCallTimestamp, 10);
        const now = Date.now();
        
        // If timestamp is recent (within last 30 seconds), consider call connected
        if (now - timestamp < 30000 && activeCallRoomName === currentRoomName) {
          if (!isConnected) {
            setIsConnected(true);
            setIsCalling(false);
            forceUpdate(prev => prev + 1);
          }
        }
      }
    };
    
    window.addEventListener('callConnected', handleCallConnected as EventListener);
    window.addEventListener('callEnded', handleCallEnded as EventListener);
    
    // Check connection status every 500ms
    const interval = setInterval(checkConnectionStatus, 500);
    
    return () => {
      window.removeEventListener('callConnected', handleCallConnected as EventListener);
      window.removeEventListener('callEnded', handleCallEnded as EventListener);
      clearInterval(interval);
    };
  }, [currentRoomName, shouldConnect, isConnected]);
  
  // Restore active call state when component mounts (prevents disconnection on tab switch)
  useEffect(() => {
    const activeCallRoomName = sessionStorage.getItem('activeCallRoomName');
    const activeCallCustomer = sessionStorage.getItem('activeCallCustomer');
    const activeCallTimestamp = sessionStorage.getItem('activeCallTimestamp');
    
    if (activeCallRoomName && activeCallCustomer && customerData && activeCallTimestamp) {
      try {
        const storedCustomer = JSON.parse(activeCallCustomer);
        const currentCustomerPhone = cleanPhoneNumber(customerData.phone || '');
        const storedCustomerPhone = cleanPhoneNumber(storedCustomer.phone || '');
        const timestamp = parseInt(activeCallTimestamp, 10);
        const now = Date.now();
        
        // Only restore if it's the same customer and call is recent (within 5 minutes)
        if (currentCustomerPhone === storedCustomerPhone && (now - timestamp < 5 * 60 * 1000)) {
          logger.log('Restoring active call state:', activeCallRoomName);
          // Set connected state immediately for UI
          setIsConnected(true);
          setIsCalling(false);
          
          // Re-fetch token to reconnect
          const participantToken = getCookie("token") || "";
          const phoneNumberId = getPhoneNumberId();
          const customerPhone = cleanPhoneNumber(customerData?.phone || "");
          
          if (phoneNumberId && customerPhone) {
            // For incoming calls: use "You" as your name, customer name shown separately
            const finalParticipantName = 'You'; // Your name when answering incoming call
            const finalParticipantIdentity = participantIdentity || `user_${customerPhone}`;
            
            fetchLiveKitToken(
              participantToken,
              activeCallRoomName,
              finalParticipantName,
              finalParticipantIdentity
            ).then((response) => {
              setToken(response.token);
              setServerUrl(response.serverUrl);
              setCurrentRoomName(activeCallRoomName);
              setShouldConnect(true);
              setIsConnected(true);
              setIsCalling(false);
            }).catch((err) => {
              logger.error('Failed to restore call:', err);
              setIsConnected(false);
              // Clear invalid state
              sessionStorage.removeItem('activeCallRoomName');
              sessionStorage.removeItem('activeCallCustomer');
              sessionStorage.removeItem('activeCallTimestamp');
            });
          }
        } else {
          // Call is stale or different customer, clear it
          if (now - timestamp >= 5 * 60 * 1000) {
            logger.log('Call state is stale, clearing');
            sessionStorage.removeItem('activeCallRoomName');
            sessionStorage.removeItem('activeCallCustomer');
            sessionStorage.removeItem('activeCallTimestamp');
          }
        }
      } catch (error) {
        logger.warn('Could not restore call state:', error);
      }
    } else {
      // No active call in storage, ensure UI shows correct state
      setIsConnected(false);
      setIsCalling(false);
    }
  }, [customerData, participantIdentity, participantName]);

  // Check for incoming call on mount and when customer data changes
  // Also check periodically to detect new incoming calls
  useEffect(() => {
    const checkIncomingCall = () => {
      // Always start by assuming no incoming call
      let hasIncoming = false;
      let incomingRoom: string | null = null;
      let callerPhone: string | null = null;
      
      const incomingCallStatus = sessionStorage.getItem('incomingCallStatus');
      const incomingCallRoomName = sessionStorage.getItem('incomingCallRoomName');
      const selectedCustomer = sessionStorage.getItem('selectedCustomer');
      const phoneNumberId = getPhoneNumberId();
      const customerPhone = cleanPhoneNumber(customerData?.phone || "");
      
      // Extract caller phone from selectedCustomer if available
      if (selectedCustomer) {
        try {
          const customer = JSON.parse(selectedCustomer);
          callerPhone = customer.phone || customer.name || null;
        } catch (error) {
          // Ignore parse errors
        }
      }
      
      // Check if there's an incoming call - be more lenient for immediate detection
      // Priority: If there's an incoming call status and room name, show "Join Call"
      // This ensures button appears immediately after redirect, even if customerData isn't loaded yet
      if (incomingCallStatus && 
          incomingCallStatus !== 'answered' && 
          incomingCallStatus !== 'ended' &&
          incomingCallRoomName) {
        
        // If we have customer data, verify room name matches
        if (phoneNumberId && customerPhone) {
          try {
            const expectedRoomName = generateRoomName(phoneNumberId, customerPhone);
            
            // Only set if the incoming call room name matches this customer exactly
            if (incomingCallRoomName === expectedRoomName) {
              hasIncoming = true;
              incomingRoom = incomingCallRoomName;
              if (!callerPhone) {
                callerPhone = customerPhone;
              }
            }
          } catch (error) {
            // If we can't generate room name, still check if room name exists
            // This handles cases where customerData isn't loaded yet after redirect
            if (incomingCallRoomName) {
              hasIncoming = true;
              incomingRoom = incomingCallRoomName;
            }
          }
        } else {
          // Customer data not loaded yet, but we have incoming call data
          // Show "Join Call" button anyway - it will be validated when customerData loads
          hasIncoming = true;
          incomingRoom = incomingCallRoomName;
          if (callerPhone) {
            // Use caller phone from selectedCustomer
          } else if (customerPhone) {
            callerPhone = customerPhone;
          }
        }
      }
      
      // Update state - explicitly set to false if no incoming call
      // Force immediate update to ensure button changes instantly
      const wasIncomingCall = hasIncomingCall;
      setHasIncomingCall(hasIncoming);
      setIncomingCallRoom(incomingRoom);
      setIncomingCallerPhone(hasIncoming ? callerPhone : null);
      
      // If this is a new incoming call, set timestamp and start auto-reject timer
      if (hasIncoming && (!wasIncomingCall || incomingRoom !== incomingCallRoom)) {
        const now = Date.now();
        setIncomingCallTimestamp(now);
        
        // Clear any existing timeout
        if (autoRejectTimeoutRef.current) {
          clearTimeout(autoRejectTimeoutRef.current);
        }
        
        // Set timeout to auto-reject after 60 seconds
        autoRejectTimeoutRef.current = setTimeout(() => {
          logger.log('Auto-rejecting call after 60 seconds timeout');
          if (hasIncomingCall && incomingCallRoom) {
            handleRejectCall();
          }
        }, 60000); // 60 seconds
      } else if (!hasIncoming) {
        // Clear timeout if call is answered/rejected/ended
        if (autoRejectTimeoutRef.current) {
          clearTimeout(autoRejectTimeoutRef.current);
          autoRejectTimeoutRef.current = null;
        }
        setIncomingCallTimestamp(null);
      }
      
      // Force re-render if state changed
      if (hasIncoming) {
        forceUpdate(prev => prev + 1);
      }
    };

    // Check immediately - use setTimeout(0) to ensure it runs after component mount
    setTimeout(() => {
      checkIncomingCall();
    }, 0);
    
    // Also check immediately after a short delay to catch any race conditions
    setTimeout(() => {
      checkIncomingCall();
    }, 100);

    // Check periodically (every 500ms) to detect new incoming calls faster
    const interval = setInterval(checkIncomingCall, 500);

    // Also listen for storage events (when incoming call data is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'incomingCallStatus' || e.key === 'incomingCallRoomName') {
        checkIncomingCall();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (for same-tab updates)
    const handleIncomingCallEvent = () => {
      checkIncomingCall();
    };
    window.addEventListener('incomingCallUpdated', handleIncomingCallEvent);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('incomingCallUpdated', handleIncomingCallEvent);
      // Clear timeout on unmount
      if (autoRejectTimeoutRef.current) {
        clearTimeout(autoRejectTimeoutRef.current);
      }
    };
  }, [customerData, isConnected, hasIncomingCall, incomingCallRoom]);
  
  // Load call records from API
  useEffect(() => {
    const loadCallRecords = async () => {
      try {
        setCallRecordsLoading(true);
        
        const phoneNumberId = getPhoneNumberId();
        if (!phoneNumberId) {
          setCallRecords([]);
          setCallRecordsLoading(false);
          return;
        }
        
        // Get current customer phone from URL or customerData FIRST
        let currentCustomerPhone: string | null = null;
        if (customerData?.phone) {
          currentCustomerPhone = cleanPhoneNumber(customerData.phone);
        } else {
          // Extract from URL: /events/whatsapp-send-message/{phoneNumber}/whatsappcalls
          const pathParts = window.location.pathname.split('/');
          const whatsappSendMessageIndex = pathParts.indexOf('whatsapp-send-message');
          if (whatsappSendMessageIndex !== -1 && pathParts[whatsappSendMessageIndex + 1]) {
            currentCustomerPhone = cleanPhoneNumber(pathParts[whatsappSendMessageIndex + 1]);
          }
        }
        
        if (!currentCustomerPhone) {
          setCallRecords([]);
          setCallRecordsLoading(false);
          return;
        }
        
        // CALL AUDIO API FIRST - it returns call records with audio URLs
        let callsData: any[] = [];
        
        try {
          // Call audio API to get call records
          const audioApiResult = await getCallRecordsFromAudioApi(phoneNumberId, currentCustomerPhone);
          
          if (audioApiResult.success && (audioApiResult.records || audioApiResult.calls || audioApiResult.data)) {
            // Audio API returned JSON with call records
            callsData = audioApiResult.records || audioApiResult.calls || audioApiResult.data || [];
          } else {
            // Audio API didn't return records, fall back to list API
            const result = await getWhatsAppCallRecords(phoneNumberId);
            callsData = result.records || result.calls || result.data || [];
            
            // Filter calls for current customer
            callsData = callsData.filter((call: any) => {
              const callerId = cleanPhoneNumber(call.caller_id || '');
              const calleeId = cleanPhoneNumber(call.callee_id || '');
              return callerId === currentCustomerPhone || calleeId === currentCustomerPhone;
            });
          }
        } catch (audioError) {
          // Fall back to list API
          const result = await getWhatsAppCallRecords(phoneNumberId);
          callsData = result.records || result.calls || result.data || [];
          
          // Filter calls for current customer
          callsData = callsData.filter((call: any) => {
            const callerId = cleanPhoneNumber(call.caller_id || '');
            const calleeId = cleanPhoneNumber(call.callee_id || '');
            return callerId === currentCustomerPhone || calleeId === currentCustomerPhone;
          });
        }
        
        if (callsData.length === 0) {
          setCallRecords([]);
          setCallRecordsLoading(false);
          return;
        }
        
        const filteredCalls = callsData;
        
        // Map filtered calls and fetch audio URLs from /audio API if recording_url is null
        const mappedRecordsPromises = filteredCalls.map(async (call: any) => {
          const duration = call.call_duration_seconds !== undefined ? call.call_duration_seconds :
                          call.duration !== undefined ? call.duration :
                          call.call_duration !== undefined ? call.call_duration :
                          call.duration_seconds !== undefined ? call.duration_seconds :
                          undefined;
          
          let status: 'completed' | 'missed' | 'failed' = 'completed';
          if (duration !== undefined && duration < 60) {
            status = 'missed';
          } else {
            const callStatus = call.call_status || call.status || 'completed';
            const statusLower = String(callStatus).toLowerCase();
            if (statusLower === 'missed') {
              status = 'missed';
            } else if (statusLower === 'failed' || statusLower === 'error') {
              status = 'failed';
            } else {
              status = 'completed';
            }
          }
          
          // Get recording URL - use from API response first
          let recordingUrl = call.recording_url;
          
          // ALWAYS try to get audio from /audio API if recording_url is null/empty
          // Use caller_id from the call record
          if ((!recordingUrl || recordingUrl === null || recordingUrl.trim() === '') && call.caller_id) {
            try {
              const callerIdForAudio = cleanPhoneNumber(call.caller_id);
              const audioUrl = await getCallAudio(phoneNumberId, callerIdForAudio);
              if (audioUrl) {
                recordingUrl = audioUrl;
              }
            } catch (audioError) {
              // Silently fail - recording URL will remain null
            }
          }
          
          const callType: 'incoming' | 'outgoing' = (call.call_direction === 'inbound' || call.call_direction === 'incoming') ? 'incoming' : 'outgoing';
          
          return {
            customerName: call.caller_name || call.customerName || call.customer_name || call.name || call.caller_id || call.callerId || 'Unknown',
            customerNumber: call.caller_id || call.callerId || call.callee_id || call.calleeId || 'Unknown',
            timestamp: call.call_start_time || call.timestamp || call.created_at || call.createdAt || new Date().toISOString(),
            duration,
            type: callType,
            status,
            callerId: call.caller_id || call.callerId,
            callId: call.callId || call.call_uuid,
            call_uuid: call.call_uuid,
            recording_url: recordingUrl || null,
            call_start_time: call.call_start_time,
            call_end_time: call.call_end_time,
            call_duration_seconds: call.call_duration_seconds,
            call_direction: call.call_direction,
            caller_name: call.caller_name || '',
          } as CallRecord;
        });
        
        // Wait for all audio URLs to be fetched
        const mappedRecords = await Promise.all(mappedRecordsPromises);
        
        // Sort by timestamp descending (newest first)
        mappedRecords.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        // Force state update
        setCallRecords(mappedRecords);
        
      } catch (error) {
        setCallRecords([]);
      } finally {
        setCallRecordsLoading(false);
      }
    };
    
    loadCallRecords();
  }, [customerData]); // Run when component mounts or customerData changes
  
  
  // Handle join for initiated outgoing call (after API call, before connecting)
  const handleJoinInitiatedCall = useCallback(() => {
    if (callInitiated && token && serverUrl && currentRoomName && !isConnected) {
      logger.log("Joining initiated call...");
      setIsCalling(true);
      setShouldConnect(true);
      // Clear outgoing call flags when user joins
      sessionStorage.removeItem('outgoingCallRoomName');
      sessionStorage.removeItem('outgoingCallTimestamp');
    }
  }, [callInitiated, token, serverUrl, currentRoomName, isConnected]);

  // Handle manual join for incoming calls
  const handleJoinIncomingCall = useCallback(() => {
    if (incomingCallRoom && hasIncomingCall) {
      // Clear auto-reject timeout since user is accepting
      if (autoRejectTimeoutRef.current) {
        clearTimeout(autoRejectTimeoutRef.current);
        autoRejectTimeoutRef.current = null;
      }
      
      const roomToJoin = incomingCallRoom;
      
      // CRITICAL: Update UI state IMMEDIATELY - use React.startTransition for immediate update
      // Set isCalling FIRST - this will make button show "Connecting..." immediately
      setIsCalling(true);
      forceUpdate(prev => prev + 1); // Force immediate re-render
      
      // Then clear incoming call state
      setHasIncomingCall(false);
      setIncomingCallRoom(null);
      setIncomingCallTimestamp(null);
      setCallError(null);
      
      // Stop notification immediately
      stopCallNotification();
      
      // Clear incoming call status immediately
      sessionStorage.setItem('incomingCallStatus', 'answered');
      clearIncomingCallData();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('incomingCallUpdated'));
      
      // Use microtask to ensure state updates are flushed before async call
      Promise.resolve().then(() => {
        // Then proceed with connecting to the call
        handleCallWithRoom(roomToJoin);
      });
    }
  }, [incomingCallRoom, hasIncomingCall, handleCallWithRoom]);

  // Listen for answer incoming call event from header notification
  useEffect(() => {
    const handleAnswerIncomingCall = () => {
      // Check if we should auto-join
      const autoJoin = sessionStorage.getItem('autoJoinIncomingCall');
      const incomingCallRoomName = sessionStorage.getItem('incomingCallRoomName');
      const incomingCallStatus = sessionStorage.getItem('incomingCallStatus');
      
      if (autoJoin === 'true' && incomingCallRoomName && incomingCallStatus && 
          incomingCallStatus !== 'answered' && incomingCallStatus !== 'ended') {
        // Clear the auto-join flag
        sessionStorage.removeItem('autoJoinIncomingCall');
        
        // Set incoming call state if not already set
        if (!hasIncomingCall) {
          setHasIncomingCall(true);
          setIncomingCallRoom(incomingCallRoomName);
        }
        
        // Wait a bit for component to be ready, then join the call
        setTimeout(() => {
          if (incomingCallRoomName) {
            handleJoinIncomingCall();
          }
        }, 500);
      }
    };

    const handleAnswerEvent = () => handleAnswerIncomingCall();
    window.addEventListener('answerIncomingCall', handleAnswerEvent);
    
    // Also check on mount if auto-join flag is set
    const autoJoin = sessionStorage.getItem('autoJoinIncomingCall');
    if (autoJoin === 'true') {
      setTimeout(handleAnswerIncomingCall, 1000);
    }

    return () => {
      window.removeEventListener('answerIncomingCall', handleAnswerEvent);
    };
  }, [hasIncomingCall, incomingCallRoom, handleJoinIncomingCall]);

  // Handle reject call - stop ringtone, clear notification, and delete room
  const handleRejectCall = useCallback(async () => {
    // Clear auto-reject timeout since user is rejecting manually
    if (autoRejectTimeoutRef.current) {
      clearTimeout(autoRejectTimeoutRef.current);
      autoRejectTimeoutRef.current = null;
    }
    
    if (incomingCallRoom) {
      const roomToDelete = incomingCallRoom;
      
      // Stop ringtone immediately
      stopCallNotification();
      
      // Clear incoming call state immediately
      setHasIncomingCall(false);
      setIncomingCallRoom(null);
      setIncomingCallerPhone(null);
      setIncomingCallTimestamp(null);
      setCallError(null);
      
      // Clear incoming call status
      sessionStorage.setItem('incomingCallStatus', 'rejected');
      clearIncomingCallData();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('incomingCallUpdated'));
      
      // Delete the room and remove all users
      try {
        await deleteRoom(roomToDelete);
        logger.log('Room deleted after rejecting call:', roomToDelete);
      } catch (error) {
        logger.error('Error deleting room after rejecting call:', error);
        // Continue even if room deletion fails
      }
    }
  }, [incomingCallRoom]);
  
  const handleCall = async () => {
    if (!customerData?.phone) return;

    // Update UI IMMEDIATELY before any async operations
    // This triggers "Connecting..." UI immediately (isCalling=true, token=null)
    setIsCalling(true);
    forceUpdate(prev => prev + 1); // Force immediate re-render
    setCallError(null);
    setHasIncomingCall(false);
    setIncomingCallRoom(null);

    try {

      const phoneNumberId = getPhoneNumberId();
      const customerPhone = cleanPhoneNumber(customerData.phone);
      
      if (!phoneNumberId) {
        throw new Error("phoneNumberId not found in sessionStorage");
      }
      if (!customerPhone) {
        throw new Error("Customer phone number not found");
      }
      
      // Call business-call API to initiate the call and wait for response
      let apiResponse;
      try {
        apiResponse = await initiateBusinessCall({
          phoneNumberId: phoneNumberId,
          to: customerPhone,
          recipientName: customerData.name || customerPhone,
        });
        logger.log("Business call API called successfully", apiResponse);
        
        // Check if API response indicates success
        const isSuccess = apiResponse?.success === true || apiResponse?.data?.success === true;
        if (!isSuccess) {
          throw new Error(apiResponse?.data?.message || apiResponse?.message || "Business call initiation failed");
        }
        
        // Only generate room name AFTER successful API response
        // This avoids creating room name if API call fails
        const generatedRoomName = generateRoomName(phoneNumberId, customerPhone);
        
        // Store outgoing call info - wait for WebSocket notification before joining
        // Similar to incoming calls - don't get token or join room yet
        sessionStorage.setItem('outgoingCallRoomName', generatedRoomName);
        sessionStorage.setItem('outgoingCallTimestamp', Date.now().toString());
        sessionStorage.setItem('outgoingCallCustomerPhone', customerPhone);
        sessionStorage.setItem('outgoingCallCustomerName', customerData.name || customerPhone);
        
        // Mark call as initiated - DON'T get token or join room yet
        // Wait for WebSocket message indicating call was answered (like incoming calls)
        setCallInitiated(true);
        setIsCalling(false); // Stop "Connecting..." - we're waiting for WebSocket notification
        setCurrentRoomName(generatedRoomName);
        
        // Store active call info for WebSocket handler
        if (customerData) {
          sessionStorage.setItem('activeCallRoomName', generatedRoomName);
          sessionStorage.setItem('activeCallCustomer', JSON.stringify(customerData));
          sessionStorage.setItem('activeCallTimestamp', Date.now().toString());
        }
        
        // Poll LiveKit rooms API every 10 seconds, maximum 20 attempts
        // Clear any existing interval first
        if (outgoingCallPollIntervalRef.current) {
          clearInterval(outgoingCallPollIntervalRef.current);
        }
        
        // Reset attempt counter
        outgoingCallPollAttemptsRef.current = 0;
        const MAX_POLL_ATTEMPTS = 20;
        const POLL_INTERVAL_MS = 10000; // 10 seconds
        
        // Store generatedRoomName in a local variable for closure
        const roomNameToPoll = generatedRoomName;
        
        // Start polling immediately, then every 10 seconds
        const checkRoomAndJoin = async () => {
          logger.log("checkRoomAndJoin called - starting check");
          
          // Increment attempt counter
          outgoingCallPollAttemptsRef.current += 1;
          
          // Get the stored room name from sessionStorage
          const storedRoomName = sessionStorage.getItem('outgoingCallRoomName');
          logger.log("Stored room name from sessionStorage:", storedRoomName);
          logger.log("Room name to poll:", roomNameToPoll);
          
          // Check if call is still initiated (not joined yet)
          // Use storedRoomName check instead of callInitiated state (which is async)
          const stillInitiated = storedRoomName === roomNameToPoll && storedRoomName !== null;
          if (!stillInitiated || !storedRoomName) {
            logger.log("Stopping polling - conditions not met:", {
              stillInitiated,
              storedRoomName,
              roomNameToPoll
            });
            // Call already joined or cancelled, stop polling
            if (outgoingCallPollIntervalRef.current) {
              clearInterval(outgoingCallPollIntervalRef.current);
              outgoingCallPollIntervalRef.current = null;
            }
            outgoingCallPollAttemptsRef.current = 0;
            return;
          }
          
          logger.log("Conditions met, proceeding with API call");
          
          // Check if we've exceeded max attempts
          if (outgoingCallPollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
            logger.log(`Max polling attempts (${MAX_POLL_ATTEMPTS}) reached. Stopping polling. Room not found.`);
            
            // Stop polling
            if (outgoingCallPollIntervalRef.current) {
              clearInterval(outgoingCallPollIntervalRef.current);
              outgoingCallPollIntervalRef.current = null;
            }
            
            // Clear call initiated state and show error
            setCallInitiated(false);
            setIsCalling(false);
            setCallError("Call was not answered. Room was not created within the expected time.");
            
            // Clear outgoing call flags
            sessionStorage.removeItem('outgoingCallRoomName');
            sessionStorage.removeItem('outgoingCallTimestamp');
            sessionStorage.removeItem('outgoingCallCustomerPhone');
            sessionStorage.removeItem('outgoingCallCustomerName');
            
            outgoingCallPollAttemptsRef.current = 0;
            return;
          }
          
          try {
            logger.log(`Polling LiveKit rooms API (attempt ${outgoingCallPollAttemptsRef.current}/${MAX_POLL_ATTEMPTS}) to check for room:`, storedRoomName);
            const roomsResponse = await listLiveKitRooms();
            
            if (roomsResponse.success && roomsResponse.rooms) {
              // Find room that matches our stored room name
              const matchingRoom = roomsResponse.rooms.find(
                (room) => room.name === storedRoomName
              );
              
              // If room exists and has participants (numParticipants > 1 means someone else joined)
              // or numPublishers > 0 means someone is publishing audio/video
              if (matchingRoom && (matchingRoom.numParticipants > 1 || matchingRoom.numPublishers > 0)) {
                logger.log("Room found with participants - auto-joining call", {
                  roomName: matchingRoom.name,
                  numParticipants: matchingRoom.numParticipants,
                  numPublishers: matchingRoom.numPublishers,
                  attempts: outgoingCallPollAttemptsRef.current
                });
                
                // Stop polling
                if (outgoingCallPollIntervalRef.current) {
                  clearInterval(outgoingCallPollIntervalRef.current);
                  outgoingCallPollIntervalRef.current = null;
                }
                
                // Reset attempt counter
                outgoingCallPollAttemptsRef.current = 0;
                
                // Join the room
                setIsCalling(true);
                const participantToken = getCookie("token") || "";
                
                if (!participantToken || !customerPhone) {
                  throw new Error("Missing token or customer phone");
                }
                
                // For outgoing calls: use "You" as your name
                const finalParticipantName = 'You';
                const finalParticipantIdentity = participantIdentity || `user_${customerPhone}`;
                
                const response = await fetchLiveKitToken(
                  participantToken,
                  storedRoomName,
                  finalParticipantName,
                  finalParticipantIdentity
                );
                
                setToken(response.token);
                setServerUrl(response.serverUrl);
                setCurrentRoomName(storedRoomName);
                setCallInitiated(false); // Clear initiated flag since we're joining now
                setShouldConnect(true);
                
                // Clear outgoing call flags
                sessionStorage.removeItem('outgoingCallRoomName');
                sessionStorage.removeItem('outgoingCallTimestamp');
                sessionStorage.removeItem('outgoingCallCustomerPhone');
                sessionStorage.removeItem('outgoingCallCustomerName');
              } else {
                logger.log(`Room not found or no participants yet (attempt ${outgoingCallPollAttemptsRef.current}/${MAX_POLL_ATTEMPTS}), will check again in 10 seconds`);
              }
            }
          } catch (err: any) {
            logger.error(`Error checking LiveKit rooms (attempt ${outgoingCallPollAttemptsRef.current}/${MAX_POLL_ATTEMPTS}):`, err);
            // Continue polling even if API call fails (unless max attempts reached)
          }
        };
        
        // Poll immediately, then every 10 seconds
        logger.log("Starting polling for outgoing call room. Will call checkRoomAndJoin immediately.");
        checkRoomAndJoin();
        outgoingCallPollIntervalRef.current = setInterval(checkRoomAndJoin, POLL_INTERVAL_MS);
        logger.log("Polling interval set up. Will check every 10 seconds.");
        
        // Clear any incoming call state
        setHasIncomingCall(false);
        setIncomingCallRoom(null);
      } catch (apiError: any) {
        logger.error("Error calling business-call API:", apiError);
        // Clean up polling interval if API call fails
        if (outgoingCallPollIntervalRef.current) {
          clearInterval(outgoingCallPollIntervalRef.current);
          outgoingCallPollIntervalRef.current = null;
        }
        // Reset attempt counter
        outgoingCallPollAttemptsRef.current = 0;
        throw new Error(apiError?.response?.data?.message || apiError?.message || "Failed to initiate business call");
      }
      
    } catch (err: any) {
      logger.error("Error initiating WhatsApp call:", err);
      // Clean up outgoing call flags on any error
      if (outgoingCallPollIntervalRef.current) {
        clearInterval(outgoingCallPollIntervalRef.current);
        outgoingCallPollIntervalRef.current = null;
      }
      // Reset attempt counter
      outgoingCallPollAttemptsRef.current = 0;
      sessionStorage.removeItem('outgoingCallRoomName');
      sessionStorage.removeItem('outgoingCallTimestamp');
      sessionStorage.removeItem('outgoingCallCustomerPhone');
      sessionStorage.removeItem('outgoingCallCustomerName');
      
      // Extract error message - check multiple possible formats
      let errorMessage = "Failed to initiate call";
      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setCallError(errorMessage);
      setIsCalling(false);
    } finally {
      setIsCalling(false);
    }
  };


  const handleRoomConnected = () => {
    // Update state immediately
    setIsConnected(true);
    setIsCalling(false);
    setCallInitiated(false); // Clear initiated state once connected
    forceUpdate(prev => prev + 1); // Force immediate re-render
    setCallError(null);
    setHasIncomingCall(false);
    setIncomingCallRoom(null);
    
    // Store active call info in sessionStorage for persistent access
    if (currentRoomName && customerData) {
      sessionStorage.setItem('activeCallRoomName', currentRoomName);
      sessionStorage.setItem('activeCallCustomer', JSON.stringify(customerData));
      sessionStorage.setItem('activeCallTimestamp', Date.now().toString());
    }
  };

  const handleRoomDisconnected = useCallback(async () => {
    // Stop polling interval if active
    if (outgoingCallPollIntervalRef.current) {
      clearInterval(outgoingCallPollIntervalRef.current);
      outgoingCallPollIntervalRef.current = null;
    }
    // Reset attempt counter
    outgoingCallPollAttemptsRef.current = 0;

    setIsConnected(false);
    setCallInitiated(false);
    setShouldConnect(false);

    // When room disconnects (we hung up OR other party hung up), always clear active call UI
    if (currentRoomName) {
      await deleteRoom(currentRoomName);
    }
    sessionStorage.removeItem('activeCallRoomName');
    sessionStorage.removeItem('activeCallCustomer');
    sessionStorage.removeItem('activeCallTimestamp');
    sessionStorage.removeItem('outgoingCallRoomName');
    sessionStorage.removeItem('outgoingCallTimestamp');
    window.dispatchEvent(new CustomEvent('callDisconnected'));

    setToken(null);
    setServerUrl("");
    setCurrentRoomName(null);
    roomRef.current = null;
  }, [currentRoomName]);

  const handleDisconnect = async () => {
    try {
      // Stop polling interval if active
      if (outgoingCallPollIntervalRef.current) {
        clearInterval(outgoingCallPollIntervalRef.current);
        outgoingCallPollIntervalRef.current = null;
      }
      // Reset attempt counter
      outgoingCallPollAttemptsRef.current = 0;
      
      const roomNameToDelete = currentRoomName;
      
      // Clear ALL call-related storage IMMEDIATELY (active call + incoming call data + outgoing call flags)
      sessionStorage.removeItem('activeCallRoomName');
      sessionStorage.removeItem('activeCallCustomer');
      sessionStorage.removeItem('activeCallTimestamp');
      sessionStorage.removeItem('incomingCallRoomName');
      sessionStorage.removeItem('incomingCallStatus');
      sessionStorage.removeItem('outgoingCallRoomName');
      sessionStorage.removeItem('outgoingCallTimestamp');
      sessionStorage.removeItem('selectedCustomer');
      sessionStorage.removeItem('phoneNumberId');
      
      // Dispatch event to immediately update ActiveCallIndicator
      window.dispatchEvent(new CustomEvent('callDisconnected'));
      
      setShouldConnect(false);
      setCallInitiated(false); // Clear initiated call state
      
      if (roomRef.current) {
        try {
          await roomRef.current.disconnect();
        } catch (err) {
          logger.warn("Room disconnect error:", err);
        }
      }
      
      if (roomNameToDelete) {
        await deleteRoom(roomNameToDelete);
      }
      
      setTimeout(() => {
        setToken(null);
        setServerUrl("");
        setIsConnected(false);
        setCallError(null);
        setCurrentRoomName(null);
        roomRef.current = null;
      }, 100);
    } catch (err: any) {
      logger.error("Error disconnecting from room:", err);
      // Force cleanup
      sessionStorage.removeItem('activeCallRoomName');
      sessionStorage.removeItem('activeCallCustomer');
      sessionStorage.removeItem('activeCallTimestamp');
      // Dispatch event to immediately update ActiveCallIndicator
      window.dispatchEvent(new CustomEvent('callDisconnected'));
      setShouldConnect(false);
      setCallInitiated(false); // Clear initiated state on disconnect
      setToken(null);
      setServerUrl("");
      setIsConnected(false);
      setCallError(null);
      setCurrentRoomName(null);
      roomRef.current = null;
    }
  };


  return (
    <div className="flex-1 flex flex-col">
      <CallHeader
        title="WhatsApp Calls 
        "
        incomingCallAvailable={hasIncomingCall && !isConnected && !isCalling}
        isCalling={isCalling}
        isConnected={isConnected}
        hasIncomingCall={hasIncomingCall && !isConnected && !isCalling}
        hasCustomer={!!customerData}
        onCall={handleCall}
        onJoinCall={
          (hasIncomingCall && incomingCallRoom) 
            ? handleJoinIncomingCall 
            : (callInitiated && !isConnected) 
            ? handleJoinInitiatedCall 
            : undefined
        }
        onRejectCall={hasIncomingCall && incomingCallRoom ? handleRejectCall : undefined}
        onDisconnect={handleDisconnect}
        incomingCallerPhone={incomingCallerPhone || undefined}
        canMakeWhatsAppCall={true}
        callInitiated={callInitiated && !isConnected}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <CallStatusView error={callError} isCalling={isCalling} hasToken={!!token}>
          {/* Note: LiveKitRoom is now handled globally by GlobalCallConnection component */}
          {/* This ensures audio connection persists when navigating away */}
          {/* Always render LiveKitRoom when we have token - it will monitor for participants */}
          {token && serverUrl && shouldConnect ? (
            <div className="flex-1 flex flex-col h-full">
              {/* @ts-ignore - LiveKitRoom has ReactNode return type which includes string, but works fine at runtime */}
              <LiveKitRoom
                video={false}
                audio={true}
                token={token}
                serverUrl={serverUrl}
                connect={shouldConnect}
                onConnected={handleRoomConnected}
                onDisconnected={handleRoomDisconnected}
                options={{
                  adaptiveStream: true,
                  dynacast: true,
                  publishDefaults: {
                    videoSimulcastLayers: [],
                  },
                }}
                className="flex-1"
              >
                {/* Monitor for remote participants and show UI when they join */}
                <RoomParticipantMonitor 
                  callInitiated={callInitiated}
                  onRemoteParticipantJoined={() => {
                    logger.log("Remote participant joined - auto-joining call");
                    setCallInitiated(false);
                    setIsCalling(false);
                    forceUpdate(prev => prev + 1);
                    // Clear outgoing call flags when remote participant joins
                    sessionStorage.removeItem('outgoingCallRoomName');
                    sessionStorage.removeItem('outgoingCallTimestamp');
                  }}
                />
                {/* Show UI only if not initiated call OR remote participant has joined */}
                {isConnected && (!callInitiated) ? (
                  <SimpleCallInterface 
                    onDisconnect={handleDisconnect} 
                    customerName={customerData?.name || 'Customer'}
                  />
                ) : null}
              </LiveKitRoom>
            </div>
          ) : token && serverUrl && shouldConnect ? (
            // Show status when monitoring for remote participant
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="text-4xl mb-4">📞</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  {callInitiated ? "Waiting for call to be answered..." : "Connecting..."}
                </h3>
                <p className="text-gray-500">
                  {callInitiated 
                    ? "Call initiated. Waiting for the other person to join..." 
                    : "Establishing call connection..."}
                </p>
              </div>
            </div>
          ) : (
            // Display call records when not in a call
            <div className="flex-1 overflow-y-auto p-4">
              {callRecordsLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="text-4xl mb-4 animate-pulse">📞</div>
                    <p className="text-gray-600">Loading call records...</p>
                  </div>
                </div>
              ) : Array.isArray(callRecords) && callRecords.length > 0 ? (
                <div className="space-y-3">
                  {callRecords.map((call, index) => {
                    const callId = call.callId || call.call_uuid || `call-${index}`;
                    const formattedDate = call.call_start_time 
                      ? new Date(call.call_start_time).toLocaleString()
                      : new Date(call.timestamp).toLocaleString();
                    const formattedDuration = call.call_duration_seconds !== undefined
                      ? `${Math.floor(call.call_duration_seconds / 60)}:${String(call.call_duration_seconds % 60).padStart(2, '0')}`
                      : call.duration !== undefined
                      ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}`
                      : '--';
                    
                    const displayName = call.caller_name || call.customerName || call.callerId || call.customerNumber || 'Unknown';
                    
                    return (
                      <div
                        key={callId}
                        className="p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
                            call.type === 'incoming' || call.call_direction === 'inbound' ? 'bg-green-500' : 'bg-blue-500'
                          }`}>
                            {call.type === 'incoming' || call.call_direction === 'inbound' ? '↓' : '↑'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">
                                {displayName}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                call.status === 'completed' ? 'bg-green-100 text-green-800' :
                                call.status === 'missed' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {call.call_direction === 'inbound' || call.call_direction === 'incoming' 
                                  ? 'Inbound' 
                                  : call.call_direction === 'outbound' || call.call_direction === 'outgoing'
                                  ? 'Outbound'
                                  : call.type === 'incoming' 
                                  ? 'Incoming' 
                                  : 'Outgoing'} - {call.status}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-2 flex flex-col gap-1">
                              <div className="flex items-center gap-3">
                                <span className="font-medium">Start:</span>
                                <span>{formattedDate}</span>
                              </div>
                              {call.call_end_time && (
                                <div className="flex items-center gap-3">
                                  <span className="font-medium">End:</span>
                                  <span>{new Date(call.call_end_time).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-3">
                                <span className="font-medium">Duration:</span>
                                <span className="font-semibold text-gray-700">{formattedDuration}</span>
                              </div>
                            </div>
                            {call.recording_url && call.recording_url.trim() !== '' ? (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <AudioPlayer
                                  src={call.recording_url}
                                  autoPlay={false}
                                  preload="metadata"
                                  showJumpControls={false}
                                  customProgressBarSection={[
                                    RHAP_UI.CURRENT_TIME,
                                    RHAP_UI.PROGRESS_BAR,
                                    RHAP_UI.DURATION,
                                  ]}
                                  customControlsSection={[
                                    RHAP_UI.MAIN_CONTROLS,
                                    RHAP_UI.VOLUME_CONTROLS,
                                  ]}
                                />
                              </div>
                            ) : (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <span className="text-sm text-gray-400">No recording available</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="text-4xl mb-4">📞</div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">WhatsApp Calls</h3>
                    <p className="text-gray-500">Call history and details will appear here</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CallStatusView>
      </div>
    </div>
  );
}

